import { prodServiceEndpointsConfig } from '@onshape/www-inertia';
import type { ServiceEndpointsQueryResult } from '~/sanity/types';
import SolrFq from '~/utils/solr/SolrFq';
import SolrQuery from '~/utils/solr/SolrQuery';

const serviceEndpointsMap = ref<ServiceEndpointsQueryResult>({
  ...prodServiceEndpointsConfig,
  firebaseDevFunctionsMap: null,
  cadEnvToCadSsoDomainMap: null,
});

const getWwwEnv = () => {
  const { host } = useRequestURL();
  const domainMap = serviceEndpointsMap.value?.wwwDomainToEnvMap;
  const found = domainMap?.find((val) => val.from === host);
  return found && found.to ? found.to : 'live';
};

const getCadEnv = () => {
  const wwwEnv = getWwwEnv();
  const envMap = serviceEndpointsMap.value?.wwwEnvToCadEnvMap;
  const found = envMap?.find((val) => val.from === wwwEnv);
  return found && found.to ? found.to : 'live';
};

const getProductDomain = (): string => {
  const cadEnv = getCadEnv();
  const cadDomainMap = serviceEndpointsMap.value?.cadEnvToCadDomainMap;
  const found = cadDomainMap?.find((val) => val.from === cadEnv);
  return found && found.to ? found.to : 'cad.onshape.com';
};

const getSsoCadDomain = (): string => {
  const cadEnv = getCadEnv();
  const cadSsoDomainMap = serviceEndpointsMap.value?.cadEnvToCadSsoDomainMap;
  const found = cadSsoDomainMap?.find((val) => val.from === cadEnv);
  return found && found.to ? found.to : 'sso.onshape.com';
};

const mapDomain = async (str: string) => {
  const env = useEnvContext();
  if (env.isProd) return str;

  await serviceEndpointsReadyPromise;

  // check if str is included in the to domains of either mapping
  const isIncludedInDomainMapping = serviceEndpointsMap.value?.cadEnvToCadDomainMap?.some(
    (mapping) => str.includes(`${mapping.to}`)
  );
  const isIncludedInSsoMapping = serviceEndpointsMap.value?.cadEnvToCadSsoDomainMap?.some(
    (mapping) => str.includes(`${mapping.to}`)
  );

  if (env.isProd || (!isIncludedInDomainMapping && !isIncludedInSsoMapping)) return str;

  const from = new URL(str);

  let to;
  if (from.host.includes('sso')) {
    to = new URL(`https://${getSsoCadDomain()}`);
  } else {
    to = new URL(`https://${getProductDomain()}`);
  }

  from.host = to.host;
  return from.toString();
};

const innerMapFbFunction = async (functionPath: string) => {
  const env = useEnvContext();
  if (env.isProd) return functionPath;

  await serviceEndpointsReadyPromise;
  const fbMap = serviceEndpointsMap.value?.firebaseDevFunctionsMap;

  const from = functionPath.split('/api/fb/')[1];
  if (!from) {
    console.warn('Function path does not contain /api/fb/:', functionPath);
    return functionPath;
  }

  const found = fbMap?.find((val) => val.from === from);
  return found && found.to ? `/api/fb/${found.to}` : functionPath;
};

const mapFbFunction = async (fbPathname: string) => {
  const mapped = await innerMapFbFunction(fbPathname);
  return new URL(`${useRequestURL().origin}${mapped}`);
};

const getMap = async () => {
  const fq = SolrFq.and().push('_type_s', 'serviceEndpoints');
  const q = SolrQuery.devObj().setFq(fq).setRange({ start: 0, rows: 1 });
  const { data: serviceEndpoints } = await useSolrQuery<ServiceEndpointsQueryResult>({ q });
  if (serviceEndpoints.value.length === 0) {
    console.warn('No serviceEndpoints found in sanity, using prod defaults');
    return;
  }

  serviceEndpointsMap.value = serviceEndpoints.value[0] ?? null;
  serviceEndpointsReady.value = !!serviceEndpointsMap.value;
};

const initHekateListeners = () => {
  if (import.meta.server) {
    return;
  }
  const { addEventListener, dispatchEvent } = useHekateEventBus();

  addEventListener('request-service-endpoints', () => {
    dispatchEvent('service-endpoints-ready', {
      ...serviceEndpointsMap.value,
    });
  });
};

let isInit = false;
const serviceEndpointsReady = ref(false);
const init = () => {
  if (isInit) {
    return;
  }
  isInit = true;

  const stopWhenReady = watch(serviceEndpointsReady, (ready) => {
    if (!ready) return;
    serviceEndpointsReadyPromiseResolver();
    stopWhenReady();
  });

  const env = useEnvContext();
  if (env.isProd) {
    serviceEndpointsReady.value = true;
    return;
  }

  getMap().then(() => {
    initHekateListeners();
  });
};

const getReadyPromise = () => {
  if (import.meta.server) {
    return {
      serviceEndpointsReadyPromiseResolver: () => {},
      serviceEndpointsReadyPromise: Promise.resolve(),
    };
  } else {
    let serviceEndpointsReadyPromiseResolver: (value: void | PromiseLike<void>) => void = () => {};
    const serviceEndpointsReadyPromise = new Promise<void>((resolve) => {
      serviceEndpointsReadyPromiseResolver = resolve;
      setTimeout(resolve, 10 * 1000);
    });

    return {
      serviceEndpointsReadyPromiseResolver,
      serviceEndpointsReadyPromise,
    };
  }
};
const { serviceEndpointsReadyPromise, serviceEndpointsReadyPromiseResolver } = getReadyPromise();

export const useServiceEndpoints = () => {
  init();

  return {
    mapDomain,
    mapFbFunction,
  };
};
